import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Allotment } from 'allotment'
import 'allotment/dist/style.css'
import { TooltipProvider } from '@/components/ui/tooltip'
import { WorkspaceHeader } from '@/components/workspace/layout/WorkspaceHeader'
import { useWorkspaceSocket } from '@/hooks/workspace/useWorkspaceSocket'
import { LiveReportDocument } from '@/components/report/LiveReportDocument'
import { ReportLibrary } from '@/components/workspace/reports/ReportLibrary'
import { NewReportDialog } from '@/components/workspace/reports/NewReportDialog'
import { ReportInspector } from '@/components/workspace/reports/ReportInspector'

export default function WorkspaceReportsPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const { events, connect, disconnect } = useWorkspaceSocket()
  const [refreshKey, setRefreshKey] = useState(0)
  const [newOpen, setNewOpen] = useState(false)
  useEffect(() => {
    connect()
    return () => disconnect()
  }, [connect, disconnect])

  return (
    <TooltipProvider delayDuration={300}>
      <div className="h-screen flex flex-col bg-background overflow-hidden">
        <div className="print:hidden">
          <WorkspaceHeader mode="reports" onRefresh={() => {}} />
        </div>
        <div className="flex-1 min-h-0">
          <Allotment>
            <Allotment.Pane preferredSize={280} minSize={220} maxSize={400}>
              <div className="h-full overflow-y-auto border-r border-border print:hidden">
                <ReportLibrary
                  selectedId={reportId ?? null}
                  onOpen={(id) => navigate(`/workspace/reports/${id}`)}
                  onNew={() => setNewOpen(true)}
                  events={events}
                  refreshKey={refreshKey}
                />
              </div>
            </Allotment.Pane>
            <Allotment.Pane>
              <div className="h-full overflow-y-auto px-4 py-4 print:overflow-visible print:h-auto">
                {reportId ? (
                  <LiveReportDocument reportId={reportId} events={events} />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Select a report from the library — or create one.
                  </div>
                )}
              </div>
            </Allotment.Pane>
            <Allotment.Pane preferredSize={320} minSize={260} maxSize={440}>
              <div className="h-full overflow-y-auto border-l border-border print:hidden">
                <ReportInspector
                  reportId={reportId ?? null}
                  events={events}
                  onDeleted={() => {
                    setRefreshKey((k) => k + 1)
                    navigate('/workspace/reports')
                  }}
                  onChanged={() => setRefreshKey((k) => k + 1)}
                />
              </div>
            </Allotment.Pane>
          </Allotment>
        </div>
        <NewReportDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          onCreated={(id) => {
            setRefreshKey((k) => k + 1)
            navigate(`/workspace/reports/${id}`)
          }}
        />
      </div>
    </TooltipProvider>
  )
}
